import { useState, FormEvent, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { validateTeamMemberForm, type TeamMemberFormData } from '@/utils/validation';
import type { Database } from '@/types/database.types';

type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface TeamMemberFormProps {
    /** Whether the form modal is open */
    isOpen: boolean;
    /** Callback when form is closed/cancelled */
    onClose: () => void;
    /** Callback when form is submitted with valid data */
    onSubmit: (data: TeamMemberFormData) => void;
    /** Existing team member data for edit mode (undefined for create mode) */
    initialData?: TeamMember;
    /** Whether the submit action is in progress */
    isLoading?: boolean;
    /** Error message from submission */
    error?: string | null;
}

/**
 * Team member form component for adding and editing team members
 * Handles both create and edit modes based on presence of initialData
 */
export const TeamMemberForm = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    isLoading = false,
    error = null,
}: TeamMemberFormProps) => {
    const isEditMode = !!initialData;

    // Form state
    const [formData, setFormData] = useState<TeamMemberFormData>({
        name: initialData?.name ?? '',
        role: initialData?.role ?? '',
    });

    // Validation errors
    const [errors, setErrors] = useState<{
        name?: string;
        role?: string;
    }>({});

    // Update form when initialData changes (for edit mode)
    // Only runs when switching between different team members
    useEffect(() => {
        setFormData({
            name: initialData?.name ?? '',
            role: initialData?.role ?? '',
        });
        setErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.id]); // Only depend on ID to avoid resetting during typing

    // Handle input changes
    const handleChange = (field: keyof TeamMemberFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field when user starts typing
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    // Handle form submission
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        // Validate form
        const validation = validateTeamMemberForm(formData);

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        // Submit valid data
        onSubmit({
            name: formData.name.trim(),
            role: formData.role as 'developer' | 'qa',
        });
    };

    // Reset form when modal closes
    const handleClose = () => {
        setFormData({
            name: '',
            role: '',
        });
        setErrors({});
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={isEditMode ? 'Edit Team Member' : 'Add Team Member'}
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* API Error Alert */}
                {error && (
                    <Alert variant="error" className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Name Input */}
                <Input
                    id="member-name"
                    label="Name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={errors.name}
                    placeholder="e.g., John Doe"
                    disabled={isLoading}
                    aria-label="Team member name"
                    required
                />

                {/* Role Selection */}
                <Select
                    id="member-role"
                    label="Role"
                    value={formData.role}
                    onChange={(value) => handleChange('role', value)}
                    options={[
                        { value: 'developer', label: 'Developer' },
                        { value: 'qa', label: 'QA Engineer' },
                    ]}
                    placeholder="Select a role..."
                    disabled={isLoading}
                    error={errors.role}
                    required
                />

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save Changes' : 'Add Member')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

TeamMemberForm.displayName = 'TeamMemberForm';

